import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { MailCheck, Loader2, CheckCircle2, RefreshCw, Pencil, X } from 'lucide-react';
import { getApiErrorMessage } from '../lib/apiError';

const POLL_MS = 5000;

const Welcome = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const verified = !!user?.email_verified;

  const handleResend = useCallback(async () => {
    setSending(true);
    try {
      await api.post('/auth/resend-verification');
      toast.success('Verification email sent — please check your inbox.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not send verification email.'));
    } finally {
      setSending(false);
    }
  }, [api]);

  const handleManualCheck = useCallback(async () => {
    setChecking(true);
    try {
      await refreshUser();
    } finally {
      setChecking(false);
    }
  }, [refreshUser]);

  const handleChangeEmail = useCallback(async (e) => {
    if (e) e.preventDefault();
    const trimmed = newEmail.trim();
    if (!trimmed) {
      toast.error('Enter your new email address.');
      return;
    }
    if (trimmed.toLowerCase() === (user?.email || '').toLowerCase()) {
      toast.error('That is already the email on file.');
      return;
    }
    setSavingEmail(true);
    try {
      const { data } = await api.post('/auth/change-pending-email', { new_email: trimmed });
      await refreshUser();
      toast.success(data?.message || 'Email updated. We just sent a fresh verification link to your new address.');
      setNewEmail('');
      setEditingEmail(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not update email.'));
    } finally {
      setSavingEmail(false);
    }
  }, [api, refreshUser, newEmail, user]);

  // Poll /auth/me so the page updates the moment the user clicks the link in another tab
  useEffect(() => {
    if (verified) return;
    const id = setInterval(() => { refreshUser(); }, POLL_MS);
    const onFocus = () => refreshUser();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [verified, refreshUser]);

  // When verification flips true, celebrate & auto-forward
  useEffect(() => {
    if (verified) {
      toast.success('✨ Email verified — you can send invoices now.');
      const t = setTimeout(() => navigate('/dashboard'), 1500);
      return () => clearTimeout(t);
    }
  }, [verified, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="welcome-page">
      <header className="p-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <Logo />
        </Link>
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="skip-to-dashboard-btn">
          Skip to dashboard
        </Button>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl space-y-6">
          {/* Expressive hero panel — solid full-saturation Blue, no gradient */}
          <div
            className="bg-accent text-accent-foreground rounded-2xl border-2 border-foreground/10 relative overflow-hidden"
            data-testid="welcome-hero"
          >
            {/* Large line-icon as a geometric graphic element (no mascot, no figurative art) */}
            <div className="absolute right-4 top-4 sm:right-10 sm:top-1/2 sm:-translate-y-1/2 opacity-90 pointer-events-none">
              {verified
                ? <CheckCircle2 className="w-16 h-16 sm:w-28 sm:h-28" strokeWidth={1.25} data-testid="welcome-verified-icon" />
                : <MailCheck className="w-16 h-16 sm:w-28 sm:h-28" strokeWidth={1.25} data-testid="welcome-mail-icon" />}
            </div>

            <div className="p-8 sm:p-12 pr-24 sm:pr-48 space-y-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] opacity-80 block">
                ✱ &nbsp; One quick step
              </span>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-[1.05]">
                {verified ? "You're verified." : 'Check your email.'}
              </h1>
              <p className="text-base sm:text-lg opacity-90 max-w-md">
                {verified ? (
                  <>All set — sending you to your dashboard now.</>
                ) : (
                  <>
                    We sent a link to <strong className="font-semibold">{user?.email}</strong>. Click it and you&rsquo;re in.
                  </>
                )}
              </p>
            </div>
          </div>

          {!verified && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                variant="outline"
                onClick={handleResend}
                disabled={sending}
                className="gap-2"
                data-testid="welcome-resend-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />}
                Resend verification email
              </Button>
              <Button
                variant="ghost"
                onClick={handleManualCheck}
                disabled={checking}
                className="gap-2"
                data-testid="welcome-check-btn"
              >
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                I&rsquo;ve verified — check now
              </Button>
            </div>
          )}

          {!verified && (
            <div className="pt-4 border-t border-foreground/10 space-y-3" data-testid="welcome-change-email-section">
              {!editingEmail ? (
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <span>Wrong email address?</span>
                  <button
                    type="button"
                    onClick={() => { setEditingEmail(true); setNewEmail(''); }}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                    data-testid="welcome-open-change-email-btn"
                  >
                    <Pencil className="w-3 h-3" /> Change email address
                  </button>
                </div>
              ) : (
                <form onSubmit={handleChangeEmail} className="space-y-3 text-left max-w-md mx-auto" data-testid="welcome-change-email-form">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="new-email" className="text-xs uppercase tracking-widest font-mono opacity-70">
                      New email address
                    </Label>
                    <button
                      type="button"
                      onClick={() => { setEditingEmail(false); setNewEmail(''); }}
                      className="text-muted-foreground hover:text-foreground"
                      data-testid="welcome-cancel-change-email-btn"
                      aria-label="Cancel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <Input
                    id="new-email"
                    type="email"
                    placeholder="you@example.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    autoFocus
                    required
                    disabled={savingEmail}
                    data-testid="welcome-new-email-input"
                  />
                    <Button
                      type="submit"
                      disabled={savingEmail || !newEmail.trim()}
                      className="w-full gap-2"
                      data-testid="welcome-save-email-btn"
                    >
                      {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />}
                      Update email &amp; resend verification
                    </Button>
                    <p className="text-[11px] text-muted-foreground text-center">
                      We&rsquo;ll invalidate the old link and send a fresh one to the new address.
                    </p>
                  </form>
                )}

                <p className="text-xs text-muted-foreground text-center">
                  Didn&rsquo;t get anything? Peek in spam, then hit resend.
                </p>
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default Welcome;
